import { create } from 'zustand';

export type PlanTodoStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface PlanTodo {
  id: string;
  content: string;
  status: PlanTodoStatus;
}

interface PlanThread {
  todos: PlanTodo[];
  planCardMessageId: string | null;
}

interface PlanStoreState {
  plans: Record<string, PlanThread>;
  updatePlan: (threadId: string, todos: PlanTodo[], merge: boolean) => void;
  getPlan: (threadId: string) => PlanThread | undefined;
  markPlanCardInjected: (threadId: string, messageId: string) => void;
  clearPlan: (threadId: string) => void;
}

export const usePlanStore = create<PlanStoreState>((set, get) => ({
  plans: {},

  updatePlan: (threadId, todos, merge) => {
    const existing = get().plans[threadId];

    let newTodos: PlanTodo[];
    if (merge && existing?.todos.length) {
      const updatesById = new Map(todos.map(t => [t.id, t]));
      // Spread update over existing so frontmatter fields (embed, description, etc.) survive
      newTodos = existing.todos.map(t => {
        const u = updatesById.get(t.id);
        return u ? { ...t, ...u } : t;
      });
      // Append any brand-new todos that don't exist yet
      todos.forEach(t => {
        if (!existing.todos.find(e => e.id === t.id)) {
          newTodos.push(t);
        }
      });
    } else {
      newTodos = [...todos];
    }

    set(state => ({
      plans: {
        ...state.plans,
        [threadId]: {
          todos: newTodos,
          planCardMessageId: existing?.planCardMessageId ?? null,
        },
      },
    }));
  },

  getPlan: (threadId) => get().plans[threadId],

  markPlanCardInjected: (threadId, messageId) => {
    set(state => ({
      plans: {
        ...state.plans,
        [threadId]: {
          todos: state.plans[threadId]?.todos ?? [],
          planCardMessageId: messageId,
        },
      },
    }));
  },

  clearPlan: (threadId) => {
    set(state => {
      const next = { ...state.plans };
      delete next[threadId];
      return { plans: next };
    });
  },
}));
